/** @format */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { createInstanceAndDocs } from "../../../API/uploadToS3";
import { createInstanceAndDocsADM } from "../../../AdminAPI/UploadToS3ADM";
import { getModel } from "../../../API/modelList";
import { getInstance } from "../../../API/DocQuery";
import fetchClient from "../../../AdminAPI/ClientList";
import fetchModel from "../../../AdminAPI/ModelADM";
import { getInstanceADM } from "../../../AdminAPI/InstancesADM";
import { getDocsKeyFromModelId, filterNewDocs } from "../../Utils/ImportUtils";

export function useImportLogic(fileInputRef) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = Cookies.get("Role");
  const idToken = Cookies.get("Token");

  const [instanceName, setInstanceName] = useState("");
  const [sFinalCustomer, setSFinalCustomer] = useState("");
  const [fileNumber, setFileNumber] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [modelList, setModelList] = useState([]);
  const [selectedModel, setSelectedModel] = useState("All");
  const [showJSONEditor, setShowJSONEditor] = useState(false);
  const [value, setValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [droppedFileNames, setDroppedFileNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [useExistingInstance, setUseExistingInstance] = useState(false);
  const [existingInstanceName, setExistingInstanceName] = useState("");
  const [existingFinalCustomer, setExistingFinalCustomer] = useState("");
  const [instanceList, setInstancesList] = useState([]);
  const [iExpectedDocs, setIExpectedDocs] = useState(0);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(null);
  const [fileJSONs, setFileJSONs] = useState([]);
  const [showFillAllModal, setShowFillAllModal] = useState(false);
  const [defaultJSONValue, setDefaultJSONValue] = useState(
    JSON.stringify({ default: "value" }, null, 2)
  );
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [selectedClient, setSelectedClient] = useState("All");
  const [modelRedirect, setSelectedRedirect] = useState("");

  // Récupération des modèles et instances selon le rôle
  useEffect(() => {
    if (role === "superadmin") {
      fetchModel(null, idToken)
        .then((data) => {
          setModelList(data);
        })
        .catch((error) => {
          console.error("Error fetching model list:", error);
        });
    } else {
      getModel(idToken)
        .then((data) => {
          setModelList(data);
        })
        .catch((error) => {
          console.error("Error fetching model list:", error);
        });

      getInstance(idToken)
        .then((instances) => {
          setInstancesList(instances);
        })
        .catch((error) => {
          console.error("Error Fetching instances list: ", error);
        });
    }
  }, [idToken, role]);

  // Calcul du nombre de slots restants pour l'ajout de fichiers
  const getRemainingSlots = useCallback(() => {
    if (useExistingInstance) {
      const instanceInfo = instanceList.find(
        (i) => i.kInstanceId === existingInstanceName
      );
      if (!instanceInfo) return 0;
      const docsAlreadyInInstance = instanceInfo.iCreatedDocs || 0;
      const allowedDocs =
        instanceInfo.allowedDocs || instanceInfo.iExpectedDocs || 0;
      return allowedDocs - docsAlreadyInInstance - selectedFiles.length;
    }
    return iExpectedDocs - selectedFiles.length;
  }, [
    useExistingInstance,
    instanceList,
    existingInstanceName,
    iExpectedDocs,
    selectedFiles.length,
  ]);

  // Récupération des types de fichiers supportés pour le modèle sélectionné
  const getSupportedFileTypes = useCallback(() => {
    const selectedModelData = modelList.find(
      (model) => model.ModelId === selectedModel
    );
    return selectedModelData ? selectedModelData.ModelSupportedFileTypes : [];
  }, [modelList, selectedModel]);

  // Mise à jour de l'état des fichiers sélectionnés
  const updateFilesState = useCallback(
    (newFiles) => {
      const existingFileNames = new Set(selectedFiles.map((file) => file.name));
      const uniqueFiles = newFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );

      const remainingSlots = getRemainingSlots();

      if (uniqueFiles.length > remainingSlots) {
        setError(
          t("You have reached the maximum number of expected documents.")
        );
        return;
      }

      setSelectedFiles((prevFiles) => [...prevFiles, ...uniqueFiles]);
      setDroppedFileNames((prev) => [
        ...prev,
        ...uniqueFiles.map((file) => file.name),
      ]);
      setFileJSONs((prev) => [...prev, ...uniqueFiles.map(() => "")]);
    },
    [selectedFiles, t, getRemainingSlots]
  );

  // Gestion du changement de modèle et de client
  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
    if (role === "superadmin") {
      fetchClient(idToken, e.target.value).then((clients) => {
        setClientList(clients);
      });
    }
  };

  // Gestion du changement de client
  const handleClientChange = (e) => {
    setSelectedClient(e.target.value);
    getInstanceADM(e.target.value, selectedModel, idToken)
      .then((instances) => {
        setInstancesList(instances);
      })
      .catch((error) => {
        console.error("Error Fetching instances list: ", error);
      });
  };

  // Gestion du changement de modèle JSON dans l'éditeur
  const onChangeJSON = useCallback((val) => {
    JSON.stringify(val);
    setValue(val);
  }, []);

  const handleNext = useCallback(() => {
    setError(null);
    if (step === 0) {
      if (selectedModel === "All") {
        setError(t("Please select a model."));
      } else if (role === "superadmin") {
        setStep(1);
      } else {
        setStep(2);
      }
    } else if (step === 1) {
      if (selectedClient === "All") {
        setError(t("Please select a client."));
      } else {
        setStep(2);
      }
    } else if (step === 2) {
      if (!useExistingInstance) {
        if (!instanceName.trim()) {
          setError(t("Please enter a name for the new instance."));
          return;
        }
        if (iExpectedDocs <= 0) {
          setError(
            t(
              "Please put an expected number of documents for this new instance."
            )
          );
          return;
        }
      }
      if (useExistingInstance && !existingInstanceName) {
        setError(t("Please select an existing instance."));
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!sFinalCustomer) {
        setError(t("Please enter a final customer name."));
      } else {
        setStep(4);
      }
    } else if (step === 4) {
      if (droppedFileNames.length === 0 || !droppedFileNames) {
        setError(t("You need to add files to your instance."));
      } else {
        setStep(5);
      }
    } else if (step === 5) {
      if (
        (!useExistingInstance &&
          (!sFinalCustomer || !selectedModel || !droppedFileNames)) ||
        (useExistingInstance &&
          (!existingInstanceName || !selectedModel || !droppedFileNames))
      ) {
        setError(t("Please fill in all fields."));
        return;
      } else {
        // Avance uniquement si tout est valide pour le dernier check
        // Dans l'original, il n'y a pas d'incrémentation ici, on reste en step 5
      }
    }
  }, [
    step,
    selectedModel,
    role,
    selectedClient,
    useExistingInstance,
    instanceName,
    iExpectedDocs,
    existingInstanceName,
    sFinalCustomer,
    droppedFileNames,
    t,
  ]);

  // Gestion de l'ouverture de l'éditeur JSON
  const handleOpenJSONEditor = useCallback(
    (index) => {
      setSelectedDocumentIndex(index);
      setValue(fileJSONs[index]);
      setShowJSONEditor(true);
    },
    [fileJSONs]
  );

  // Gestion de la fermeture de l'éditeur JSON avec validation
  const handleCloseJSONEditor = useCallback(() => {
    try {
      if (!value || value.trim() === "") {
        setError(t("JSON cannot be empty."));
        return;
      }

      JSON.parse(value);

      // Si pas d'erreur, on enregistre et on ferme le modal
      setFileJSONs((prev) => {
        const updatedJSONs = [...prev];
        updatedJSONs[selectedDocumentIndex] = value;
        return updatedJSONs;
      });

      // Réinitialise l'erreur et ferme le modal
      setError(null);
      setShowJSONEditor(false);
      setSelectedDocumentIndex(null);
    } catch (error) {
      setError(t("JSON isn't valid. Please correct it."));
    }
  }, [value, selectedDocumentIndex, t]);

  const handleFileSubmit = async () => {
    // Vérification de validation finale
    if (
      (!useExistingInstance &&
        (!sFinalCustomer || !selectedModel || !droppedFileNames)) ||
      (useExistingInstance &&
        (!existingInstanceName || !selectedModel || !droppedFileNames))
    ) {
      setError(t("Please fill in all fields."));
      return;
    }

    setLoading(true);
    try {
      const numberOfDocs = selectedFiles.length;
      const adjustedIExpectedDocs =
        iExpectedDocs > 0 ? iExpectedDocs : numberOfDocs;

      if (useExistingInstance) {
        const instanceInfo = instanceList.find(
          (i) => i.kInstanceId === existingInstanceName
        );
        const docsAlreadyInInstance = instanceInfo?.docsCount || 0;
        const docsToAdd = selectedFiles.length;
        const allowedDocs = instanceInfo?.allowedDocs || Infinity;
        if (docsAlreadyInInstance + docsToAdd > allowedDocs) {
          setError(
            `L'instance sélectionnée permet seulement ${allowedDocs} documents. Actuellement ${docsAlreadyInInstance}, vous essayez d'en ajouter ${docsToAdd}.`
          );
          setLoading(false);
          return;
        }
        if (role === "superadmin") {
          await createInstanceAndDocsADM(
            existingInstanceName,
            selectedModel,
            selectedFiles,
            idToken,
            false,
            fileJSONs.slice(docsAlreadyInInstance),
            null,
            sFinalCustomer,
            selectedClient,
            existingFinalCustomer
          );
        } else {
          await createInstanceAndDocs(
            existingInstanceName,
            selectedModel,
            selectedFiles,
            idToken,
            false,
            fileJSONs.slice(docsAlreadyInInstance),
            null,
            sFinalCustomer,
            existingFinalCustomer
          );
        }
      } else {
        if (role === "superadmin") {
          await createInstanceAndDocsADM(
            instanceName,
            selectedModel,
            selectedFiles,
            idToken,
            true,
            fileJSONs,
            adjustedIExpectedDocs,
            sFinalCustomer,
            selectedClient,
            existingFinalCustomer
          );
        } else {
          await createInstanceAndDocs(
            instanceName,
            selectedModel,
            selectedFiles,
            idToken,
            true,
            fileJSONs,
            adjustedIExpectedDocs,
            sFinalCustomer,
            existingFinalCustomer
          );
        }
      }
      //set modelid
      Cookies.set("modelId", selectedModel);
      //condition set instancename
      if (!existingInstanceName) {
        Cookies.set("instanceName", instanceName);
      }
      setSelectedRedirect(selectedModel);
      setLoading(false);
      setStep(6);
      setSFinalCustomer("");
      setExistingInstanceName("");
      setFileNumber(null);
      setSelectedModel("All");
      setSelectedClient("All");
      setSelectedFiles([]);
      setDroppedFileNames([]);
      setFileJSONs([]);
    } catch (error) {
      console.error("Erreur lors de l'ajout des fichiers :", error);
      setLoading(false);
      setError(t("Error in processing, please try again."));
    }
  };

  // Button pour remplir tous les JSON avec une valeur par défaut
  const handleFillAllJSON = () => {
    setShowFillAllModal(true);
  };

  // Button pour supprimer le JSON d’un fichier spécifique
  const handleRemoveJSON = useCallback(
    (index) => {
      const updatedJSONs = [...fileJSONs];
      updatedJSONs[index] = "";
      setFileJSONs(updatedJSONs);
    },
    [fileJSONs]
  );

  // Button pour réinitialiser tous les JSON
  const handleResetJSONs = () => {
    setFileJSONs(fileJSONs.map(() => ""));
    setIsAutoFilled(false);
  };

  // Gestion du drag and drop des fichiers
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Gestion du drop des fichiers
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const files = Array.from(e.dataTransfer.files);
      const supportedFileTypes = getSupportedFileTypes();

      const validFiles = files.filter((file) =>
        supportedFileTypes.includes(file.name.split(".").pop().toLowerCase())
      );

      // La logique originale d'unicité et de slots est conservée dans updateFilesState
      const existingFileNames = new Set(selectedFiles.map((file) => file.name));
      const uniqueFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );

      updateFilesState(uniqueFiles);
    },
    [getSupportedFileTypes, updateFilesState, selectedFiles]
  );

  //   Gestion de la sélection de fichiers via l'input
  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  // Suppression du dernier fichier ajouté
  const handleFileDelete = () => {
    if (
      selectedFiles.length > 0 &&
      droppedFileNames.length - 1 !== "doc already created"
    ) {
      setSelectedFiles((prev) => prev.slice(0, -1));
      setDroppedFileNames((prev) => prev.slice(0, -1));
      setFileJSONs((prev) => prev.slice(0, -1));
    }
  };

  // Gestion de l'upload de fichiers via l'input
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    const supportedFileTypes = getSupportedFileTypes();
    const validFiles = files.filter((file) =>
      supportedFileTypes.includes(file.name.split(".").pop().toLowerCase())
    );

    if (validFiles.length !== files.length) {
      setError(t("Some files are not supported."));
    }

    const existingNames = new Set(selectedFiles.map((file) => file.name));
    const uniqueNewFiles = validFiles.filter(
      (file) => !existingNames.has(file.name)
    );

    // Vérifier les emplacements restants (via updateFilesState)
    updateFilesState(uniqueNewFiles);

    event.target.value = null;
  };

  // Logique pour mettre à jour l'état de l'instance existante (handleExistingInstanceChange)
  const handleExistingInstanceChange = (e) => {
    const selectedInstanceId = e.target.value;
    const selectedInstance = instanceList.find(
      (instance) => instance.kInstanceId === selectedInstanceId
    );
    const t_doc_already_created = t("doc already created");

    if (selectedInstance) {
      Cookies.set("instanceName", selectedInstance.sInstanceName);
      setExistingInstanceName(selectedInstanceId);
      setExistingFinalCustomer(selectedInstance.sFinalCustomer);
      setSFinalCustomer(selectedInstance.sFinalCustomer);
      setIExpectedDocs(selectedInstance.iExpectedDocs || 0);
      // Remplir les documents déjà créés
      setDroppedFileNames(
        Array(selectedInstance.iCreatedDocs || 0).fill(t_doc_already_created)
      );
      setSelectedFiles([]);
      setFileJSONs(Array(selectedInstance.iCreatedDocs || 0).fill(""));
    } else {
      setExistingInstanceName("");
      setExistingFinalCustomer("");
      setSFinalCustomer("");
      setIExpectedDocs(0);
      setDroppedFileNames([]);
      setSelectedFiles([]);
      setFileJSONs([]);
    }
  };

  // Logique pour appliquer le même JSON à tous les fichiers
  const handleApplyAllJSON = () => {
    setError(null);
    if (!defaultJSONValue || defaultJSONValue.trim() === "") {
      setError("Le JSON ne peut pas être vide.");
      return;
    }
    try {
      JSON.parse(defaultJSONValue);
      const firstNewDocIndex = droppedFileNames.findIndex(
        (name) => name !== t("doc already created")
      );
      const startIndex = firstNewDocIndex !== -1 ? firstNewDocIndex : 0; // Si pas de 'doc already created', on commence à 0.

      setFileJSONs((prevJSONs) =>
        prevJSONs.map((json, index) =>
          index >= startIndex ? defaultJSONValue : json
        )
      );

      setIsAutoFilled(true);
      setShowFillAllModal(false);
    } catch {
      setError("Le format du JSON est invalide. Veuillez corriger la syntaxe.");
    }
  };

  const finalRoute =
    role === "superadmin"
      ? `/Admin`
      : `/User/${getDocsKeyFromModelId(modelRedirect)}`;
  const filteredFileNames = filterNewDocs(droppedFileNames);

  const state = {
    instanceName,
    sFinalCustomer,
    error,
    step,
    modelList,
    selectedModel,
    showJSONEditor,
    value,
    selectedFiles,
    droppedFileNames,
    loading,
    useExistingInstance,
    existingInstanceName,
    existingFinalCustomer,
    instanceList,
    iExpectedDocs,
    selectedDocumentIndex,
    fileJSONs,
    showFillAllModal,
    defaultJSONValue,
    isAutoFilled,
    role,
    clientList,
    selectedClient,
  };

  const handlers = {
    setInstanceName,
    setSFinalCustomer,
    setIExpectedDocs,
    setSelectedModel,
    setSelectedClient,
    setUseExistingInstance,
    setStep,
    setError,
    setShowFillAllModal,
    setDefaultJSONValue,
    setDroppedFileNames,
    setSelectedFiles,
    setFileJSONs,
    handleModelChange,
    handleClientChange,
    onChangeJSON,
    handleNext,
    handleFileSubmit,
    handleOpenJSONEditor,
    handleCloseJSONEditor,
    handleFillAllJSON,
    handleRemoveJSON,
    handleResetJSONs,
    handleDragOver,
    handleDrop,
    handleFileSelect,
    handleFileDelete,
    handleFileUpload,
    handleExistingInstanceChange,
    handleApplyAllJSON,
  };

  const computed = {
    t,
    finalRoute,
    filteredFileNames,
    getSupportedFileTypes,
    getRemainingSlots,
  };

  return { state, handlers, computed };
}
